import React, { useEffect, useState } from "react";
import { Pressable, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useNavigation, useRoute } from "@react-navigation/native";
import tw from "twrnc";

import { useEventAuth } from "@/components/teams/useWorkspaceAuth";
import { useBuy } from "@/components/BuyContext";
import { useFrame } from "@/components/layout/FrameContext";
import Text from "@/components/shared/AppText";
import Button from "@/components/settings/Button";
import Callout from "@/components/settings/Callout";
import { FieldLabel } from "@/components/settings/Field";
import { SettingsPage } from "@/components/settings/SettingsPage";
import { colors, text } from "@/components/settings/theme";
import { t, tc } from "@/localization";
import { rememberRoomCopy } from "./roomCopies";
import {
  DEFAULT_ROOM_NOUN,
  ROOM_NOUNS,
  canAddRoom,
  forNoun,
  isRoom,
  roomCostText,
} from "./rooms";
import {
  CAMERA_NOUN,
  CAMERA_PRODUCT,
  DEFAULT_CAMERA_TIER,
  addsCameras,
  cameraTierOf,
  tierLengthOf,
} from "./multicam";
import { moveEventScreenBelow } from "./switchInPlace";
import useAddRoom from "./useAddRoom";
import useRoomGroup from "./useRoomGroup";

const Point = ({ children }) => (
  <View style={tw`flex-row items-start mb-2`}>
    <Ionicons
      name="checkmark-circle"
      size={16}
      color={colors.success}
      style={tw`mr-2 mt-[2px]`}
    />
    <Text style={[text.body, tw`flex-1`]}>{children}</Text>
  </View>
);

// This screen's sentences for each noun, written out so a translation can
// agree with it ("una sala nueva", "un escenario nuevo"); English reads as the
// sentences built from the noun did. A camera has its own screen below, so it
// only needs its name, the button and why one can't be added.
const ADD_ROOM_WORDS = {
  Room: () => ({
    name: tc("room noun", "Room"),
    add: (index) => t("Add Room {index}", { index }),
    cantAdd: t(
      "Rooms can't be added to this event: it's a test, it has ended, or it already has the most rooms an event can have."
    ),
    title: t("Stream several rooms at once"),
    description: t(
      "Add rooms when your event happens in more than one place at the same time: stages, tracks, courts or rooms."
    ),
    ownStream: (chat) =>
      chat
        ? t(
            "Each room has its own stream key, recording and chat. Stream it from its own phone, camera or OBS."
          )
        : t(
            "Each room has its own stream key, recording and guestbook. Stream it from its own phone, camera or OBS."
          ),
    switchBetween: t(
      "Guests keep using one link and switch between rooms on the event page. They register or buy a ticket once for all of them."
    ),
    ticketsCover: t(
      "Tickets can cover every room, or each room can sell its own (Sell Tickets)."
    ),
    becomes: (title) =>
      t('"{title}" becomes Room 1 and keeps the main link.', { title }),
    newOne: (index) =>
      t(
        "The new one is Room {index}, with the same date, look and settings. You can rename it later.",
        { index }
      ),
  }),
  Stage: () => ({
    name: tc("room noun", "Stage"),
    add: (index) => t("Add Stage {index}", { index }),
    cantAdd: t(
      "Stages can't be added to this event: it's a test, it has ended, or it already has the most stages an event can have."
    ),
    title: t("Stream several stages at once"),
    description: t(
      "Add stages when your event happens in more than one place at the same time: stages, tracks, courts or rooms."
    ),
    ownStream: (chat) =>
      chat
        ? t(
            "Each stage has its own stream key, recording and chat. Stream it from its own phone, camera or OBS."
          )
        : t(
            "Each stage has its own stream key, recording and guestbook. Stream it from its own phone, camera or OBS."
          ),
    switchBetween: t(
      "Guests keep using one link and switch between stages on the event page. They register or buy a ticket once for all of them."
    ),
    ticketsCover: t(
      "Tickets can cover every stage, or each stage can sell its own (Sell Tickets)."
    ),
    becomes: (title) =>
      t('"{title}" becomes Stage 1 and keeps the main link.', { title }),
    newOne: (index) =>
      t(
        "The new one is Stage {index}, with the same date, look and settings. You can rename it later.",
        { index }
      ),
  }),
  Track: () => ({
    name: tc("room noun", "Track"),
    add: (index) => t("Add Track {index}", { index }),
    cantAdd: t(
      "Tracks can't be added to this event: it's a test, it has ended, or it already has the most tracks an event can have."
    ),
    title: t("Stream several tracks at once"),
    description: t(
      "Add tracks when your event happens in more than one place at the same time: stages, tracks, courts or rooms."
    ),
    ownStream: (chat) =>
      chat
        ? t(
            "Each track has its own stream key, recording and chat. Stream it from its own phone, camera or OBS."
          )
        : t(
            "Each track has its own stream key, recording and guestbook. Stream it from its own phone, camera or OBS."
          ),
    switchBetween: t(
      "Guests keep using one link and switch between tracks on the event page. They register or buy a ticket once for all of them."
    ),
    ticketsCover: t(
      "Tickets can cover every track, or each track can sell its own (Sell Tickets)."
    ),
    becomes: (title) =>
      t('"{title}" becomes Track 1 and keeps the main link.', { title }),
    newOne: (index) =>
      t(
        "The new one is Track {index}, with the same date, look and settings. You can rename it later.",
        { index }
      ),
  }),
  Court: () => ({
    name: tc("room noun", "Court"),
    add: (index) => t("Add Court {index}", { index }),
    cantAdd: t(
      "Courts can't be added to this event: it's a test, it has ended, or it already has the most courts an event can have."
    ),
    title: t("Stream several courts at once"),
    description: t(
      "Add courts when your event happens in more than one place at the same time: stages, tracks, courts or rooms."
    ),
    ownStream: (chat) =>
      chat
        ? t(
            "Each court has its own stream key, recording and chat. Stream it from its own phone, camera or OBS."
          )
        : t(
            "Each court has its own stream key, recording and guestbook. Stream it from its own phone, camera or OBS."
          ),
    switchBetween: t(
      "Guests keep using one link and switch between courts on the event page. They register or buy a ticket once for all of them."
    ),
    ticketsCover: t(
      "Tickets can cover every court, or each court can sell its own (Sell Tickets)."
    ),
    becomes: (title) =>
      t('"{title}" becomes Court 1 and keeps the main link.', { title }),
    newOne: (index) =>
      t(
        "The new one is Court {index}, with the same date, look and settings. You can rename it later.",
        { index }
      ),
  }),
  Field: () => ({
    name: tc("room noun", "Field"),
    add: (index) => t("Add Field {index}", { index }),
    cantAdd: t(
      "Fields can't be added to this event: it's a test, it has ended, or it already has the most fields an event can have."
    ),
    title: t("Stream several fields at once"),
    description: t(
      "Add fields when your event happens in more than one place at the same time: stages, tracks, courts or rooms."
    ),
    ownStream: (chat) =>
      chat
        ? t(
            "Each field has its own stream key, recording and chat. Stream it from its own phone, camera or OBS."
          )
        : t(
            "Each field has its own stream key, recording and guestbook. Stream it from its own phone, camera or OBS."
          ),
    switchBetween: t(
      "Guests keep using one link and switch between fields on the event page. They register or buy a ticket once for all of them."
    ),
    ticketsCover: t(
      "Tickets can cover every field, or each field can sell its own (Sell Tickets)."
    ),
    becomes: (title) =>
      t('"{title}" becomes Field 1 and keeps the main link.', { title }),
    newOne: (index) =>
      t(
        "The new one is Field {index}, with the same date, look and settings. You can rename it later.",
        { index }
      ),
  }),
  Hall: () => ({
    name: tc("room noun", "Hall"),
    add: (index) => t("Add Hall {index}", { index }),
    cantAdd: t(
      "Halls can't be added to this event: it's a test, it has ended, or it already has the most halls an event can have."
    ),
    title: t("Stream several halls at once"),
    description: t(
      "Add halls when your event happens in more than one place at the same time: stages, tracks, courts or rooms."
    ),
    ownStream: (chat) =>
      chat
        ? t(
            "Each hall has its own stream key, recording and chat. Stream it from its own phone, camera or OBS."
          )
        : t(
            "Each hall has its own stream key, recording and guestbook. Stream it from its own phone, camera or OBS."
          ),
    switchBetween: t(
      "Guests keep using one link and switch between halls on the event page. They register or buy a ticket once for all of them."
    ),
    ticketsCover: t(
      "Tickets can cover every hall, or each hall can sell its own (Sell Tickets)."
    ),
    becomes: (title) =>
      t('"{title}" becomes Hall 1 and keeps the main link.', { title }),
    newOne: (index) =>
      t(
        "The new one is Hall {index}, with the same date, look and settings. You can rename it later.",
        { index }
      ),
  }),
  Camera: () => ({
    name: t("Camera"),
    add: (index) => t("Add Camera {index}", { index }),
    cantAdd: t(
      "Cameras can't be added to this event: it's a test, it has ended, or it already has the most cameras an event can have."
    ),
  }),
};

// What a personal account's next camera costs: CAMERA_PRODUCT for each of
// the event's days, whatever the main camera's replay, which it keeps. Rooms
// are created with the account's credits when it has enough
// (createEventOrTest), so a prepaid event pays for it.
const cameraCostText = (
  primary,
  auth,
  getCurrencyPrice,
  pricingReady,
  nextIndex
) => {
  const days = Math.max(1, Number(primary?.days) || 1);
  if ((Number(auth?.credits) || 0) >= days) {
    return days > 1
      ? t("Adding it uses {days} of your prepaid event days.", { days })
      : t("Adding it uses one of your prepaid events.");
  }
  const price = pricingReady ? getCurrencyPrice(CAMERA_PRODUCT, days) : "";
  if (!primary?.is_published) {
    return price
      ? t(
          "Each extra camera is just {price}. You'll unlock them together with your event.",
          { price }
        )
      : t(
          "Extra cameras cost less than your event. You'll unlock them together with your event."
        );
  }
  const tier = cameraTierOf(primary) || DEFAULT_CAMERA_TIER;
  const length = tierLengthOf(tier);
  return price
    ? t(
        "Camera {index} is just {price} and keeps the same {length} replay as your main camera. You'll unlock it after adding it.",
        { index: nextIndex, price, length }
      )
    : t(
        "Camera {index} keeps the same {length} replay as your main camera. You'll unlock it after adding it.",
        { index: nextIndex, length }
      );
};

// Add a room: what rooms are, what to call them, what they cost. A screen of the
// event (route AddRoom) rather than a dialog, so on wide layouts it opens in the
// pane next to the event menu like any other setting. `event` is the screen's
// event: the primary or any of its rooms. Personal accounts add cameras: an
// optional second (or third) phone on the same event, no name to choose.
const AddRoomPane = ({ event }) => {
  const navigation = useNavigation();
  const route = useRoute();
  const frame = useFrame();
  // Teams: the plan and prices of the event's account.
  const auth = useEventAuth(event);
  const { getCurrencyPrice, pricingReady } = useBuy();
  const { add, adding } = useAddRoom({ navigation });
  const { primary: groupPrimary, rooms } = useRoomGroup(event);
  const primary = isRoom(event) ? groupPrimary : event;
  const personalCameras = addsCameras(auth);

  const nounLocked = !!primary?.room_noun || personalCameras;
  const startNoun = personalCameras
    ? CAMERA_NOUN
    : primary?.room_noun || DEFAULT_ROOM_NOUN;
  const [noun, setNoun] = useState(startNoun);
  useEffect(() => {
    setNoun(startNoun);
  }, [startNoun]);

  if (!primary?.slug || !primary.title) return null;

  const roomCount = rooms.length;
  const nextIndex = roomCount + 2;
  const words = forNoun(ADD_ROOM_WORDS, noun);
  const cameras = noun === CAMERA_NOUN;
  const addable = canAddRoom(primary, auth, roomCount);

  // The new room's overview: in the pane (wide layouts), or the event screen
  // this one was opened from (phones), so Back from there goes to the dashboard.
  const openRoom = (room) => {
    if (frame?.switchRoom) {
      frame.switchRoom(room, { section: "EventEdit" });
    } else if (moveEventScreenBelow(navigation, route.key, event.slug, room)) {
      navigation.goBack();
    } else {
      rememberRoomCopy(room);
      navigation.replace("EventEdit", { slug: room.slug });
    }
  };
  const close = () => {
    if (frame?.switchRoom) {
      frame.switchRoom(event, { section: "EventEdit" });
    } else if (navigation.canGoBack()) {
      navigation.goBack();
    }
  };
  const onAdd = async () => {
    const room = await add(primary, noun);
    if (room?.slug) openRoom(room);
  };

  const addButton = addable ? (
    <View style={tw`mt-6`}>
      <Button
        text={words.add(nextIndex)}
        variant="primary"
        icon="add"
        loading={adding}
        onPress={onAdd}
      />
      <Button
        text={t("Not now")}
        variant="tertiary"
        fullWidth={false}
        style={tw`self-center mt-2`}
        disabled={adding}
        onPress={close}
      />
    </View>
  ) : (
    <Text style={[text.hint, tw`mt-6`]}>{words.cantAdd}</Text>
  );

  const nounPicker = !nounLocked && (
    <>
      <FieldLabel label={t("What do you call them?")} style={tw`mt-4`} />
      <View style={tw`flex-row flex-wrap`}>
        {ROOM_NOUNS.map((option) => {
          const selected = option === noun;
          return (
            <Pressable
              key={option}
              accessibilityRole="radio"
              accessibilityState={{ selected }}
              onPress={() => setNoun(option)}
              style={[
                tw`px-3 py-1.5 mr-2 mb-2 rounded-full border`,
                selected
                  ? { backgroundColor: colors.slate, borderColor: colors.slate }
                  : { borderColor: colors.border },
              ]}
            >
              <Text
                style={[
                  tw`text-[13px] font-semibold`,
                  selected ? tw`text-white` : { color: colors.ink },
                ]}
              >
                {forNoun(ADD_ROOM_WORDS, option).name}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </>
  );

  // Cameras: angles of the same moment, sharing the main camera's link,
  // guestbook and download page.
  if (cameras) {
    return (
      <SettingsPage
        title={
          personalCameras ? t("Add another camera angle") : t("Add a camera")
        }
        description={t(
          "Film the same moment from another angle! Guests watch on your one link and switch between the angles in the player."
        )}
      >
        <Point>
          {t("Use more than one phone, tablet or camera at the same time.")}
        </Point>
        <Point>
          {t(
            "Guests can switch the angles on the same event page during Live and in Recording."
          )}
        </Point>
        <Point>{t("If one stops, the other cameras keep streaming.")}</Point>
        <Point>
          {t(
            "Every camera keeps its own recording, so you get every angle afterwards."
          )}
        </Point>
        {nounPicker}
        <Callout tone="neutral" style={tw`mt-4`}>
          <Text style={text.body}>
            {roomCount === 0
              ? `${t('"{title}" becomes Camera 1 and keeps its link.', {
                  title: primary.title,
                })} `
              : ""}
            {t(
              "The new one is Camera {index}. Stream it from another phone, yours or a friend's: How to Go Live has a code to scan.",
              { index: nextIndex }
            )}
          </Text>
          <Text style={[text.bodyStrong, tw`mt-2`]}>
            {personalCameras
              ? cameraCostText(
                  primary,
                  auth,
                  getCurrencyPrice,
                  pricingReady,
                  nextIndex
                )
              : roomCostText(primary, auth)}
          </Text>
        </Callout>
        {addButton}
      </SettingsPage>
    );
  }

  return (
    <SettingsPage title={words.title} description={words.description}>
      <Point>{words.ownStream(!!primary.prefer_chat)}</Point>
      <Point>{words.switchBetween}</Point>
      <Point>{words.ticketsCover}</Point>
      {nounPicker}
      <Callout tone="neutral" style={tw`mt-4`}>
        <Text style={text.body}>
          {roomCount === 0 ? `${words.becomes(primary.title)} ` : ""}
          {words.newOne(nextIndex)}
        </Text>
        <Text style={[text.bodyStrong, tw`mt-2`]}>
          {roomCostText(primary, auth)}
        </Text>
      </Callout>
      {addButton}
    </SettingsPage>
  );
};

export default AddRoomPane;
